import { Spacing } from '@persona-id/design-tokens';
import { MergeIcon } from '@persona-id/design-tokens/icons';
import { ReactElement, ReactNode, VFC } from 'react';
import styled from 'styled-components';

import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplateScreenPreview, {
  PREVIEW_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import Tooltip from 'dashboard/components/Tooltip';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import useToggle from 'lib/hooks/useToggle';
import type { StepPreview } from 'lib/interfaces/StepsConfig';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import { ThemeAttributes } from 'verify/models/theme';

export const CONNECTOR_NODE_SIZE = 162;
const ICON_SIZE = 46;
const SCREEN_PREVIEW_SCALE_FACTOR = 0.33;

const NodeWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  pointer-events: auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${Spacing[1]};
  padding-top: 0px;
  text-align: center;
`;

const Title = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  margin-bottom: ${Spacing[1]};
`;

const StepPreviewWrapper = styled.div.attrs<{ screenDimensions?: ScreenDimensions }>((props) => ({
  style: {
    width: `${(props?.screenDimensions?.width ?? PREVIEW_WIDTH) * SCREEN_PREVIEW_SCALE_FACTOR}px`,
    height: `${(props?.screenDimensions?.height ?? FRAME_HEIGHT) * SCREEN_PREVIEW_SCALE_FACTOR}px`,
  },
}))<{ screenDimensions?: ScreenDimensions }>`
  display: flex;
  justify-content: center;
  align-items: center;

  transform: scale(${SCREEN_PREVIEW_SCALE_FACTOR * 100}%);
  & > * {
    cursor: auto;
    user-select: none;
  }
`;

const NodeButton = styled(Button).attrs(() => ({ type: 'button' }))<{
  selected: boolean;
  hasError?: boolean;
}>`
  height: ${CONNECTOR_NODE_SIZE}px;
  width: ${CONNECTOR_NODE_SIZE}px;
  padding: ${Spacing[3]};
  border: 2px solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  box-shadow: ${Elevation.Mid};
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  z-index: ${Z_INDICES.withShadow};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-color: ${({ theme, selected, hasError }) => {
    if (hasError) {
      return theme.color.variants.danger.highlight;
    } else if (selected) {
      return theme.color.primary.brand600;
    } else {
      return theme.color.variants.primary.highlight;
    }
  }};
  svg {
    width: ${ICON_SIZE}px;
    height: ${ICON_SIZE}px;
    color: ${({ theme }) => theme.color.primary.brand600};
  }
  &[aria-disabled='true'] {
    pointer-events: none;
    cursor: default;
  }
`;

interface ConnectorNodeProps {
  title: string;
  theme: ThemeAttributes;
  previewPage: ReactElement;
  stepPreview: StepPreview;
  isShowingDialog?: boolean;
  editable?: boolean;
  selected?: boolean;
  hasError?: boolean;
  screenDimensions?: ScreenDimensions;
  nodeHighlighter?: ReactNode;
}

/**
 * This component is responsible for rendering a ConnectorNode
 * representing routing to a screen step already rendered in the tree
 */
const ConnectorNode: VFC<ConnectorNodeProps> = ({
  title,
  theme,
  previewPage,
  stepPreview,
  isShowingDialog,
  editable,
  screenDimensions,
  nodeHighlighter,
  hasError = false,
  selected = false,
}) => {
  const panZoomBoxControls = usePanZoomBoxControls();
  const isTransforming = panZoomBoxControls?.isTransforming ?? false;
  const [isHovered, { enable: activate, disable: deactivate }] = useToggle(false);
  return (
    <NodeWrapper
      data-test={`${title}__connector-node`}
      onMouseEnter={activate}
      onMouseLeave={deactivate}
    >
      {nodeHighlighter}
      <Tooltip
        placement='top'
        tooltipShown={isHovered && !isTransforming ? !isShowingDialog : false}
        tooltip={
          <ContentWrapper>
            <Title>Go to {title}</Title>
            <StepPreviewWrapper screenDimensions={screenDimensions}>
              <InquiryTemplateScreenPreview
                theme={theme}
                page={previewPage}
                step={stepPreview}
                screenDimensions={screenDimensions}
              />
            </StepPreviewWrapper>
          </ContentWrapper>
        }
      >
        <NodeButton aria-disabled={!editable} selected={selected} hasError={hasError}>
          <MergeIcon size={24} />
        </NodeButton>
      </Tooltip>
    </NodeWrapper>
  );
};

export default ConnectorNode;
